import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const DateWiseCallCenterSurveyDash = () => {
    // --- State ---
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // --- THE FIX: Dynamically get the current month in "YYYY-MM" format ---
    const today = new Date();
    const currentYearMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    
    const [selectedMonth, setSelectedMonth] = useState(currentYearMonth);

    // --- Hooks & Context ---
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Date Wise Survey",
            subtitle: "Call center survey statistics per day",
            selectMonth: "Select Month:",
            loadingData: "Loading Data...",
            noData: "No data available for the selected month.",
            totalSurveyed: "Total Surveyed"
        },
        hi: {
            pageTitle: "तिथि वार सर्वेक्षण",
            subtitle: "प्रतिदिन कॉल सेंटर सर्वेक्षण आंकड़े",
            selectMonth: "महीना चुनें:",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "चयनित महीने के लिए कोई डेटा उपलब्ध नहीं है।",
            totalSurveyed: "कुल सर्वेक्षित"
        },
        gu: {
            pageTitle: "તારીખ વાઈઝ સર્વે",
            subtitle: "દરરોજ કૉલ સેન્ટર સર્વે આંકડા",
            selectMonth: "મહિનો પસંદ કરો:",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "પસંદ કરેલ મહિના માટે કોઈ ડેટા ઉપલબ્ધ નથી.",
            totalSurveyed: "કુલ સર્વેક્ષણ"
        }
    };

    const txt = t[lang];

    // Helper function to convert "2026-03" to "Mar,2026" for the API
    const formatMonthForApi = (yyyyMm) => {
        if (!yyyyMm) return "";
        const [year, month] = yyyyMm.split('-');
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthName = monthNames[parseInt(month, 10) - 1];
        return `${monthName},${year}`;
    };

    // --- NEW: Helper function to convert "30 Mar, 2026" to "30,MAR,2026" for navigation ---
    const handleCardClick = (dateString) => {
        // Remove the comma and split by space
        const cleanStr = dateString.replace(',', ''); 
        const parts = cleanStr.split(' '); 
        
        let formattedDate = dateString;
        if (parts.length === 3) {
            // parts[0] = "30", parts[1] = "Mar", parts[2] = "2026"
            formattedDate = `${parts[0]},${parts[1].toUpperCase()},${parts[2]}`;
        }
        
        // Pass the formatted date to your target page
        // (Change '/date_wise_survey_details' to your actual target route)
        navigate('/dis_date_wise_call_center_survey_voter', { state: { date: formattedDate } });
    };

    // --- API Fetch Logic ---
    const fetchDateData = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const payloadMonth = formatMonthForApi(selectedMonth);
            
            const response = await axios.post(
                `${BASE_URL}/api/get_voter_survey_count_by_month_only_call_center`,
                { 
                    app_id: appId,
                    month: payloadMonth
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setData(response.data.data.result);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error fetching date wise survey data:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // Re-fetch whenever the selected month changes
    useEffect(() => {
        fetchDateData();
    }, [appId, selectedMonth]);

    // --- Loading State ---
    if (loading && data.length === 0) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #f8fafc; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header
                title={txt.pageTitle}
            />

            {/* Month/Year Picker Row */}
            <div className="filter-container mt-3 mb-4">
                <div className="month-picker-wrapper" style={{height:"50px"}}>
                    <label className="month-picker-label">{txt.selectMonth}</label>
                    <input
                        type="month"
                        className="month-input-modern"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                </div>
                
                <div className="result-count d-none d-sm-block">
                    <span className="count-badge">{data.length}</span> Days
                </div>
            </div>

            {/* Loading Overlay for Background Fetching */}
            {loading && data.length > 0 && (
                <div className="text-center text-primary mb-3">
                    <small>{txt.loadingData}</small>
                </div>
            )}

            {/* No Data State */}
            {data.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">📅</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- DATA GRID --- */}
            <div className="dashboard-grid pb-5">
                {data.map((item, index) => (
                    <div 
                        className="survey-stat-card" 
                        key={index}
                        onClick={() => handleCardClick(item.date)}
                        style={{ cursor: 'pointer' }}
                    >
                        
                        <div className="card-header-row text-center border-0 pb-0">
                            <h3 className="date-title">{item.date}</h3>
                        </div>

                        <div className="card-body-row mt-3">
                            <div className="stats-wrapper w-100">
                                <div className="m-stat-box primary-box py-4">
                                    <span className="m-val c-total large-val">{item.total}</span>
                                    <span className="m-lbl">{txt.totalSurveyed}</span>
                                </div>
                            </div>
                        </div>

                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- FILTER / PICKER STYLE --- */
                .filter-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .month-picker-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: white;
                    padding: 8px 16px;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .month-picker-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #475569;
                    margin: 0;
                }
                .month-input-modern {
                    border: none;
                    background: transparent;
                    font-size: 15px;
                    font-weight: 700;
                    color: #1e293b;
                    outline: none;
                    cursor: pointer;
                }
                .month-input-modern::-webkit-calendar-picker-indicator {
                    cursor: pointer;
                    filter: invert(0.4) sepia(1) saturate(5) hue-rotate(180deg);
                }
                
                .result-count {
                    font-size: 14px; font-weight: 600; color: #64748b;
                }
                .count-badge {
                    background: #e2e8f0; color: #1e293b; padding: 4px 10px; border-radius: 12px;
                }

                /* --- DASHBOARD GRID --- */
                .dashboard-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 20px;
                }

                /* --- CARD STYLES --- */
                .survey-stat-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    transition: all 0.3s ease;
                    display: flex;
                    flex-direction: column;
                }
                .survey-stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.08);
                    border-color: #e2e8f0;
                }

                .card-header-row {
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 12px;
                }
                .date-title {
                    font-size: 1.2rem;
                    font-weight: 800;
                    color: #3b82f6; /* Blue tint for the date */
                    margin: 0;
                }

                .card-body-row {
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                .stats-wrapper {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                }

                .m-stat-box {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 10px 4px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    border: 1px solid #e2e8f0;
                }
                
                .primary-box {
                    background: #eff6ff; /* Light blue background */
                    border-color: #bfdbfe;
                }

                .m-val {
                    font-weight: 900;
                    line-height: 1.2;
                }
                .large-val {
                    font-size: 32px;
                }
                
                .m-lbl {
                    font-size: 12px;
                    font-weight: 700;
                    color: #64748b;
                    margin-top: 4px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .c-total { color: #1e40af; } /* Dark Blue for total */

                .empty-state { background: white; border-radius: 20px; border: 2px dashed #e2e8f0; }
                .empty-icon { font-size: 3rem; margin-bottom: 10px; opacity: 0.5; }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .filter-container { 
                        flex-direction: column; 
                        align-items: stretch; 
                        gap: 16px; 
                    }
                    .month-picker-wrapper {
                        justify-content: space-between;
                    }
                    .dashboard-grid { 
                        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); 
                    }
                }
            `}</style>
        </div>
    );
};

export default DateWiseCallCenterSurveyDash;